import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import { TbTableOff } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useNivoChartTheme } from '@/hooks/useNivoChartTheme';
import { EmptySection } from '@/shared';
import PlatformTable from '@/shared/components/tables/PlatformTable';

import {
    MAX_VIEWABLE_VANTAGE_ROWS,
    type VantageQueryResult,
    type VantageResultDensity,
    type VantageVisualizationType,
} from './types';
import VantageCodeBlock from './VantageCodeBlock';

interface VantageResultViewProps {
    result?: VantageQueryResult | null;
    visualization?: VantageVisualizationType;
    density?: VantageResultDensity;
    fitContainer?: boolean;
}

const isNumeric = (value: unknown): value is number =>
    typeof value === 'number' || (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value)));

const compareResultValues = (left: unknown, right: unknown) => {
    if (left === right) {
        return 0;
    }
    if (left === null || left === undefined) {
        return 1;
    }
    if (right === null || right === undefined) {
        return -1;
    }

    if (isNumeric(left) && isNumeric(right)) {
        return Number(left) - Number(right);
    }

    return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
};

const COUNT_METRIC_KEY = 'record_count';
const TABLE_CHROME_HEIGHT = 104;
const MIN_TABLE_BODY_HEIGHT = 80;

const VantageResultView: FC<VantageResultViewProps> = ({
    result,
    visualization = 'table',
    density = 'comfortable',
    fitContainer = false,
}) => {
    const { token } = useAntDesignTheme();
    const { nivoChartTheme, colorsArray } = useNivoChartTheme();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerHeight, setContainerHeight] = useState(0);

    const rows = useMemo(() => (result?.rows ?? []).slice(0, MAX_VIEWABLE_VANTAGE_ROWS), [result?.rows]);
    const columns = useMemo(() => result?.columns ?? [], [result?.columns]);
    const tableColumnWidth = density === 'compact' ? 144 : 168;
    const tableScrollX = Math.max(columns.length * tableColumnWidth, tableColumnWidth);
    const tableScrollY = fitContainer ? Math.max(MIN_TABLE_BODY_HEIGHT, containerHeight - TABLE_CHROME_HEIGHT) : 320;

    useEffect(() => {
        if (!fitContainer || !containerRef.current) {
            return;
        }

        const node = containerRef.current;
        const updateContainerHeight = () => {
            setContainerHeight(node.getBoundingClientRect().height);
        };
        const resizeObserver = new ResizeObserver(updateContainerHeight);

        updateContainerHeight();
        resizeObserver.observe(node);

        return () => {
            resizeObserver.disconnect();
        };
    }, [fitContainer]);

    const tableColumns = useMemo(
        () =>
            columns.map((column) => ({
                title: column.name,
                dataIndex: column.name,
                key: column.name,
                ellipsis: true,
                render: (value: unknown) => (value === null || value === undefined ? '' : String(value)),
                sorter: (left: Record<string, unknown>, right: Record<string, unknown>) =>
                    compareResultValues(left[column.name], right[column.name]),
                sortDirections: ['ascend' as const, 'descend' as const],
                width: tableColumnWidth,
            })),
        [columns, tableColumnWidth],
    );

    const chartFields = useMemo(() => {
        const numericColumns = columns.filter((column) => rows.some((row) => isNumeric(row[column.name])));
        const dimension =
            columns.find((column) =>
                rows.some(
                    (row) =>
                        row[column.name] !== null && row[column.name] !== undefined && !isNumeric(row[column.name]),
                ),
            )?.name ??
            columns.find((column) => !numericColumns.some((numericColumn) => numericColumn.name === column.name))
                ?.name ??
            columns[0]?.name;
        const metric = numericColumns.find((column) => column.name !== dimension)?.name;

        return { dimension, metric, usesCountMetric: !metric };
    }, [columns, rows]);

    const chartData = useMemo(() => {
        if (!chartFields.dimension) {
            return [];
        }

        if (chartFields.usesCountMetric) {
            const countsByDimension = new Map<string, number>();

            rows.forEach((row, index) => {
                const dimensionValue = row[chartFields.dimension!];
                const key =
                    dimensionValue === null || dimensionValue === undefined
                        ? `Row ${index + 1}`
                        : String(dimensionValue);

                countsByDimension.set(key, (countsByDimension.get(key) ?? 0) + 1);
            });

            return [...countsByDimension.entries()].map(([key, count]) => ({
                id: key,
                label: key,
                [COUNT_METRIC_KEY]: count,
                value: count,
            }));
        }

        return rows.map((row) => ({
            id: String(row[chartFields.dimension!] ?? ''),
            label: String(row[chartFields.dimension!] ?? ''),
            [chartFields.metric!]: Number(row[chartFields.metric!] ?? 0),
            value: Number(row[chartFields.metric!] ?? 0),
        }));
    }, [chartFields.dimension, chartFields.metric, chartFields.usesCountMetric, rows]);
    const chartMetricKey = chartFields.metric ?? COUNT_METRIC_KEY;
    const jsonPayload = useMemo(
        () =>
            JSON.stringify(
                {
                    columns,
                    rows,
                    rowCount: result?.rowCount ?? 0,
                    runtimeMs: result?.runtimeMs ?? 0,
                },
                null,
                2,
            ),
        [columns, result?.rowCount, result?.runtimeMs, rows],
    );

    if (!result || !rows.length) {
        return (
            <EmptySection
                height={fitContainer ? '100%' : 16 * 16}
                icon={<TbTableOff size='1.25rem' />}
                label='No Query Results'
                description='Run a query with returned rows to populate this view.'
            />
        );
    }

    if (visualization === 'json') {
        return <VantageCodeBlock code={jsonPayload} language='json' />;
    }

    if (visualization === 'bar' && chartFields.dimension && chartData.length) {
        return (
            <ResponsiveBar
                data={chartData}
                keys={[chartMetricKey]}
                indexBy='id'
                margin={{ top: 24, right: 24, bottom: 48, left: 64 }}
                padding={0.3}
                colors={colorsArray}
                theme={nivoChartTheme}
                axisBottom={{ tickRotation: -30, truncateTickAt: 18 }}
                axisLeft={{ truncateTickAt: 0 }}
                valueFormat={(value) => Number(value).toLocaleString()}
            />
        );
    }

    if (visualization === 'line' && chartFields.dimension && chartData.length) {
        return (
            <ResponsiveLine
                data={[
                    {
                        id: chartMetricKey,
                        data: chartData.map((row) => ({
                            x: String(row.id),
                            y: Number(row.value ?? 0),
                        })),
                    },
                ]}
                margin={{ top: 24, right: 24, bottom: 48, left: 64 }}
                colors={colorsArray}
                theme={nivoChartTheme}
                useMesh
                axisBottom={{ tickRotation: -30, truncateTickAt: 18 }}
                axisLeft={{ truncateTickAt: 0 }}
                yFormat={(value) => Number(value).toLocaleString()}
            />
        );
    }

    if (visualization === 'pie' && chartFields.dimension && chartData.length) {
        return (
            <ResponsivePie
                data={chartData}
                margin={{ top: 24, right: 96, bottom: 24, left: 96 }}
                colors={colorsArray}
                theme={nivoChartTheme}
                innerRadius={0.025}
                padAngle={1}
                cornerRadius={3}
                valueFormat={(value) => Number(value).toLocaleString()}
                enableArcLabels={false}
                enableArcLinkLabels={false}
            />
        );
    }

    return (
        <div ref={containerRef} style={{ height: '100%', minWidth: 0, overflow: 'hidden', width: '100%' }}>
            <PlatformTable
                size='small'
                rowKey={(_, index) => String(index)}
                columns={tableColumns}
                dataSource={rows}
                pagination={{ pageSize: density === 'compact' ? 50 : 25, size: 'small' }}
                scroll={{ x: tableScrollX, y: tableScrollY }}
                style={{ borderTop: `1px solid ${token.colorBorder}`, height: '100%', width: '100%' }}
            />
        </div>
    );
};

export default VantageResultView;
